/* eslint-disable hfr-tokens/no-hardcoded-design-tokens -- intentional display-size font (1.75rem) for agreement modal header icon */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import GavelIcon from '@mui/icons-material/Gavel';
import agreementService from '@/services/agreementService';
import AgreementPdfViewer, { openAgreementPdfInNewTab } from '@/components/agreements/AgreementPdfViewer';
import { TOUCH, FONT_WEIGHT, ZINDEX } from '@/core/platform/platformStandards';
import {
  agreementModalPaperSx,
  agreementModalTitleSx,
  agreementModalContentSx,
  agreementPdfRegionSx,
  agreementModalActionsSx,
} from '@/components/agreements/agreementModalLayout';

const TITLE = 'Revenue Share Agreement';
const SUBTITLE = 'HFRConnect Revenue Share Agreement';

const ViewRevShareModal = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewerInstanceKey, setViewerInstanceKey] = useState(0);
  const pdfViewSessionRef = useRef('');

  useEffect(() => {
    if (!open) {
      setPdfUrl(null);
      return;
    }

    const fetchPdf = async () => {
      try {
        setIsLoading(true);
        setError('');
        let response;
        try {
          response = await agreementService.getAgreementPersonalizedPdfUrl('revshare');
        } catch (personalizedErr) {
          console.warn('Personalized rev-share PDF unavailable, falling back to master:', personalizedErr);
          response = await agreementService.getAgreementPdfUrl('revshare');
        }
        setPdfUrl(response.url || null);
        if (!response.url) {
          setError(`Failed to load ${TITLE}. Please try again.`);
        }
      } catch (err) {
        console.error('Error fetching rev-share PDF:', err);
        setError(err.message || `Failed to load ${TITLE}. Please try again.`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPdf();
  }, [open]);

  useEffect(() => {
    if (!open) {
      pdfViewSessionRef.current = '';
      return;
    }
    if (!pdfUrl || isLoading) {
      return;
    }
    if (pdfViewSessionRef.current === pdfUrl) {
      return;
    }
    pdfViewSessionRef.current = pdfUrl;
    setViewerInstanceKey((key) => key + 1);
  }, [open, pdfUrl, isLoading]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      sx={{ zIndex: ZINDEX.modal + 100 }}
      PaperProps={{
        sx: agreementModalPaperSx(isMobile, theme),
      }}
    >
      <DialogTitle sx={agreementModalTitleSx}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
            <GavelIcon
              sx={{
                mr: 1.5,
                color: 'primary.main',
                fontSize: '1.75rem',
                flexShrink: 0,
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: FONT_WEIGHT.semibold, lineHeight: 1.25 }}>
                {TITLE}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {SUBTITLE}
              </Typography>
            </Box>
          </Box>

          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
            {pdfUrl && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<OpenInNewIcon />}
                onClick={() => openAgreementPdfInNewTab(pdfUrl)}
                disabled={isLoading}
                sx={{
                  minHeight: TOUCH.minSize,
                  display: { xs: 'none', sm: 'inline-flex' },
                }}
              >
                Open in new tab
              </Button>
            )}
            <IconButton
              onClick={onClose}
              aria-label="Close"
              sx={{ minWidth: TOUCH.minSize, minHeight: TOUCH.minSize, color: 'text.secondary' }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={agreementModalContentSx}>
        <Box sx={agreementPdfRegionSx}>
          {open && (
            <AgreementPdfViewer
              pdfUrl={pdfUrl}
              isLoading={isLoading}
              error={error}
              title={TITLE}
              minHeight="100%"
              viewerInstanceKey={viewerInstanceKey}
              active={open}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          ...agreementModalActionsSx,
          flexWrap: 'wrap',
          justifyContent: isMobile ? 'stretch' : 'flex-end',
        }}
      >
        {pdfUrl && isMobile && (
          <Button
            onClick={() => openAgreementPdfInNewTab(pdfUrl)}
            disabled={isLoading}
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            fullWidth
            sx={{ minHeight: TOUCH.minSize, mb: 0.5 }}
          >
            Open in new tab
          </Button>
        )}
        <Button
          onClick={onClose}
          variant="contained"
          fullWidth={isMobile}
          sx={{ minHeight: TOUCH.minSize, minWidth: { sm: 120 } }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ViewRevShareModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ViewRevShareModal;
